import React, { useState, useEffect, useCallback } from "react";
import { Container, Button, Modal, Form } from "react-bootstrap";
import { Box } from "@mui/material";
import { DataGrid, GridToolbarQuickFilter, gridClasses } from "@mui/x-data-grid";
import DynamicBreadcrumb from "../header/breadcrumb";
import Loader from "../loader/loader";
import menuApi from "../../services/menuapi";
import { showConfirm } from "react-confirm-prompt";
import RoleCreateForm from "./RoleCreateForm";
import LogModal from "./LogModel";
import { FiEdit2, FiFileText, FiGitBranch, FiPlus } from "react-icons/fi";
import "./RoleSettings.css";
import MenuRoleAssigner from "./MenuRoleAssigner";

const RoleSearchToolbar = () => <div className="roles-toolbar"><GridToolbarQuickFilter placeholder="Search roles..." fullWidth debounceMs={350} /></div>;

const AllRoles = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusUpdating, setStatusUpdating] = useState(false); // New loader state for status update
    const [totalRoles, setTotalRoles] = useState(0);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    const [sortModel, setSortModel] = useState([]);
    const [filterModel, setFilterModel] = useState({ items: [] });
    const [refreshTable, setRefreshTable] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingRoleId, setEditingRoleId] = useState(null);
    const [openInactivateModal, setOpenInactivateModal] = useState(false);
    const [deactivationReason, setDeactivationReason] = useState("");
    const [selectedRoleId, setSelectedRoleId] = useState(null);
    const [selectedRoleStatus, setSelectedRoleStatus] = useState(null);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [menuSaving, setMenuSaving] = useState(false);
    const [menuDirty, setMenuDirty] = useState(false);
    const [loadError, setLoadError] = useState("");

    const [roleLogs, setRoleLogs] = useState([]);
    const [showLogModal, setShowLogModal] = useState(false);
    const [showAssignMenuModal, setShowAssignMenuModal] = useState(false);
    const [selectedRoleForMenu, setSelectedRoleForMenu] = useState(null);

    const [roleLogsStatusTypes, setRoleLogsStatusTypes] = useState({
        1: "Active",
        2: "Inactive",
        3: "Blocked",
        9: "Deleted"
    });

    const pathArray = [{ name: "Role Management", url: null }];

    // Fetch roles
    const fetchRoles = useCallback(async () => {
        setLoading(true);
        setLoadError("");
        try {
            const { page, pageSize } = paginationModel;
            const data = await menuApi.getRoles({
                limit: pageSize,
                page,
                sortModel,
                filterModel,
            });

            if (data.status !== "SUCCESS") throw new Error(data.message || "Unable to fetch roles.");
            const formatted = data.data.result.map((role, index) => ({
                id: role.id,
                sno: index + 1 + page * pageSize,
                name: role.name,
                slug: role.slug,
                status: role.status,
            }));

            setRows(formatted);
            setTotalRoles(data.data.totalRecords || 0);
        } catch (err) {
            setLoadError(err.message || "Unable to fetch roles.");
        } finally {
            setLoading(false);
        }
    }, [paginationModel, sortModel, filterModel]);

    useEffect(() => {
        fetchRoles();
    }, [fetchRoles, refreshTable]);

    const handleStatusClick = (id, status) => {
        const nextStatus = status === 1 ? 2 : 1;
        const isDeactivating = nextStatus === 2;

        const confirmText = isDeactivating
            ? "Are you sure you want to inactivate this role?"
            : "Are you sure you want to activate this role?";
        const description = isDeactivating
            ? "This will make the role inactive."
            : "This will activate the role.";

        showConfirm(confirmText, {
            description,
            type: "info",
        }).then((confirmed) => {
            if (confirmed) {
                setSelectedRoleId(id);
                setSelectedRoleStatus(nextStatus);
                if (isDeactivating) {
                    setOpenInactivateModal(true);
                } else {
                    updateRoleStatus(id, nextStatus);
                }
            }
        });
    };

    const updateRoleStatus = async (id, status, reason = null) => {
        setStatusUpdating(true);
        setIsSubmitting(true); // Disable button
        try {
            const result = await menuApi.updateRoleStatus({
                id,
                status,
                reason,
                action: "status",
            });

            if (result.status === "SUCCESS") {
                showConfirm("Success", {
                    description: result.message || "Role status updated.",
                    type: "success",
                    hideCancel: true,
                });
                setRefreshTable((prev) => !prev);
            } else {
                throw new Error(result.message || "Update failed");
            }
        } catch (e) {
            showConfirm("Error", {
                description: e.message || "Something went wrong.",
                type: "warning",
                hideCancel: true,
            });
        } finally {
            setOpenInactivateModal(false);
            setDeactivationReason("");
            setErrors({});
            setStatusUpdating(false);
            setIsSubmitting(false); // Enable button again
        }
    };

    const handleInactivateSubmit = () => {
        if (!deactivationReason.trim()) {
            setErrors({ reason: "Reason is required" });
            return;
        }
        updateRoleStatus(selectedRoleId, selectedRoleStatus, deactivationReason);
    };

    const fetchRoleLogs = async (roleId) => {
        try {
            const response = await menuApi.getRoleStatusLog({ id: roleId, page: 1, pageSize: 5 });
            if (response.status === "SUCCESS") {
                setRoleLogs(response.data.result);
                setRoleLogsStatusTypes(response.data.statusTypes);
                setShowLogModal(true);
            } else {
                throw new Error(response.message || "Failed to fetch logs");
            }
        } catch (error) {
            console.error("Error fetching role logs:", error);
            showConfirm("Error", {
                description: error.message || "An error occurred while fetching logs.",
                type: "warning",
                hideCancel: true,
            });
        }
    };

    const closeMenuModal = () => {
        if (menuSaving || (menuDirty && !window.confirm("Discard unsaved menu changes?"))) return;
        setMenuDirty(false);
        setShowAssignMenuModal(false);
        setSelectedRoleForMenu(null);
    };
    const columns = [
        { field: "name", headerName: "Role Name", flex: 1, minWidth: 200 },
        { field: "status", headerName: "Status", width: 170,
            renderCell: params => <button type="button" disabled={statusUpdating}
                className={`role-status role-status-${Number(params.value) === 1 ? 'active' : 'inactive'}`}
                onClick={() => handleStatusClick(params.id, Number(params.value))}>
                {Number(params.value) === 1 ? "Active" : "Inactive"}
            </button>
        },
        { field: "actions", headerName: "Actions", flex: 1.3, minWidth: 360, sortable: false, filterable: false,
            renderCell: params => <div className="role-row-actions">
                <Button size="sm" variant="light" className="role-neutral" onClick={() => { setEditingRoleId(params.id); setShowCreateModal(true); }}><FiEdit2 /> Edit</Button>
                <Button size="sm" variant="light" className="role-neutral" onClick={() => fetchRoleLogs(params.id)}><FiFileText /> View Log</Button>
                <Button size="sm" variant="outline-success" onClick={() => { setSelectedRoleForMenu(params.row); setShowAssignMenuModal(true); }}><FiGitBranch /> Assign Menus</Button>
            </div>
        }
    ];

    return (
        <Container className="roles-page mt-4 mb-4">
            <DynamicBreadcrumb pathArray={pathArray} />
            <div className="d-flex justify-content-between align-items-center roles-heading">
                <div><h4>Roles</h4><p className="roles-subtitle">Manage roles and menu access.</p></div>
                <div className="d-flex gap-2">
                    <Button variant="success"
                        onClick={() => {
                            setEditingRoleId(null);
                            setShowCreateModal(true);
                        }}
                    >
                        <FiPlus /> Create Role
                    </Button>
                </div>
            </div>

            {(loading || statusUpdating) && <Loader />}

            {loadError && <div role="alert" className="alert alert-danger">{loadError} <Button variant="outline-danger" size="sm" onClick={fetchRoles}>Retry</Button></div>}
            <Box>
                <DataGrid
                    autoHeight
                    rows={rows}
                    columns={columns}
                    paginationMode="server"
                    sortingMode="server"
                    filterMode="server"
                    rowHeight={64}
                    pageSizeOptions={[5, 10, 20]}
                    rowCount={totalRoles}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    onSortModelChange={setSortModel}
                    onFilterModelChange={setFilterModel}
                    loading={loading}
                    disableRowSelectionOnClick
                    slots={{ toolbar: RoleSearchToolbar }}
                    slotProps={{
                        toolbar: {
                            showQuickFilter: true,
                            printOptions: { disableToolbarButton: true },
                            csvOptions: { disableToolbarButton: true },
                        },
                    }}
                    sx={{
                        [`& .${gridClasses.cell}`]: { py: 0.5 },
                    }}
                />
            </Box>

            {showCreateModal && (
                <RoleCreateForm
                    show={showCreateModal}
                    handleClose={() => {
                        setShowCreateModal(false);
                        setEditingRoleId(null);
                    }}
                    handleRefresh={() => setRefreshTable((prev) => !prev)}
                    editingRoleId={editingRoleId}
                />
            )}

            {/* Inactivation Reason Modal */}
            <Modal show={openInactivateModal} onHide={() => setOpenInactivateModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Enter Inactivation Reason</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group>
                        <Form.Label>Reason</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={deactivationReason}
                            onChange={(e) => setDeactivationReason(e.target.value)}
                            isInvalid={!!errors.reason}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.reason}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="danger"
                        onClick={handleInactivateSubmit}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? "updating..." : "Submit"}
                    </Button>
                    <Button variant="secondary" onClick={() => setOpenInactivateModal(false)}>
                        Cancel
                    </Button>
                </Modal.Footer>
            </Modal>
            <LogModal
                show={showLogModal}
                onHide={() => setShowLogModal(false)}
                logs={roleLogs}
                statusTypes={roleLogsStatusTypes} // Pass status types here
            />

            <Modal show={showAssignMenuModal} onHide={closeMenuModal} size="xl" className="role-settings-modal"
                backdrop="static" keyboard={!menuSaving} centered>
                <Modal.Header closeButton={!menuSaving}>
                    <div><Modal.Title>Assign Menus to Role</Modal.Title>
                        <div className="role-modal-subtitle">Role: {selectedRoleForMenu?.name}</div>
                    </div>
                </Modal.Header>
                <Modal.Body>
                    {selectedRoleForMenu && <MenuRoleAssigner key={selectedRoleForMenu.id} roleId={selectedRoleForMenu.id}
                        onClose={closeMenuModal} onBusyChange={setMenuSaving} onDirtyChange={setMenuDirty} />}
                </Modal.Body>
            </Modal>


        </Container>
    );
};

export default AllRoles;

